import dayjs from "dayjs";
import { validateStrongPassword } from "utils/utils";
import { getRules } from "../Utils/FormBuilderUtils";

export const formBuilderDefinedRules = {
  noLeadingWhitespace: {
    rule: (value) => !/^\s/.test(value),
    errorMessage: "First letter can not be whitespace.",
  },
  noQuotesOrBackticks: {
    rule: (value) => !/["`]/.test(value),
    errorMessage: "Wrong Format",
  },
  noLineBreaks: {
    rule: (value) => !/[\r\n]/.test(value),
    errorMessage: "No line breaks are allowed.",
  },
  noChineseCharacters: {
    rule: (value) => !/[\u4e00-\u9fa5]/.test(value),
    errorMessage:
      "Only accept Alphabet characters, numbers, symbols and spaces.",
  },
  onlyAlphabetAndSpaces: {
    rule: (value) => /^[a-zA-Z\s]*$/.test(value),
    errorMessage: "Only accept Alphabet characters and spaces.",
  },
  onlyAlphabetNumAndSpaces: {
    rule: (value) => /^[a-zA-Z\d\s]*$/.test(value),
    errorMessage: "Only accept Alphabet characters, numbers and spaces.",
  },
  onlyAlphabetNumChineseAndSpaces: {
    rule: (value) => /^[\u4e00-\u9fa5a-zA-Z0-9 ]*$/.test(value),
    errorMessage:
      "Only accept Alphabet characters, Chinese characters, numbers and spaces.",
  },

  onlyInteger: {
    rule: (value) => {
      if (typeof value !== "number" && !value) return true;
      return /^\d*$/.test(value)
    },
    errorMessage: "Only accept integers",
  },
  onlyNonZeroInteger: {
    rule: (value) => {
      if (typeof value !== "number" && !value) return true;
      return /^\d+$/.test(value) && value !== "0";
    },
    errorMessage: "Only accept non-zero integers",
  },
  onlyNumber: {
    rule: (value) => {
      if (typeof value !== "number" && !value) return true;
      return /^-?\d*\.?\d+$/.test(value);
    },
    errorMessage: "Only accept numbers",
  },
  onlyPositiveNumber: {
    rule: (value) => {
      if (typeof value !== "number" && !value) return true;
      return /^\d*\.?\d+$/.test(value);
    },
    errorMessage: "Only accept positive numbers",
  },
  onlyBlockLetter: {
    rule: (value) => /^[^a-z]*$/.test(value),
    errorMessage: "Please enter in block letters",
  },
  onlyBeforeToday: {
    rule: (value) => !dayjs(value).isAfter(dayjs()),
    errorMessage: "Please select a date before today.",
  },
  onlyAfterToday: {
    rule: (value) => !dayjs(value).isBefore(dayjs()),
    errorMessage: "Please select a date after today.",
  },

  validDate: {
    rule: (value) => dayjs(value).isValid(),
    errorMessage: "Please select a valid date.",
  },
  validPhoneNumber: {
    rule: (value) => /^[2-9][0-9]{7}$/.test(value),
    errorMessage: "The phone number is in wrong format",
  },
  validEmail: {
    rule: (value) =>
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value),
    errorMessage: "Please enter a valid email address",
  },
  validBusinessRegNo: {
    rule: (value) => /^[0-9]{8}(-?[0-9]){3}$/.test(value),
    errorMessage: "BR Wrong Format.",
  },
  validBankAccount: {
    rule: (value) => {
      if (!value) return true;
      const regex1 = /^\d{3}-\d{3}-\d{7,16}$/; // Standard format: 123-456-7890123456
      const regex2 = /^016-\d{7,16}$/; // Special case: 016-7890123456
      return regex1.test(value) || regex2.test(value);
    },
    errorMessage: "Wrong bank account format",
  },
  validBankBeneficiary: {
    rule: (value) => /^[a-zA-Z0-9()><&\".,'+\-\/!#$%*;=?@\[\]^_`{|}: ]+$/.test(value),
    errorMessage: "Wrong Bank Beneficiary Format",
  },
  validShareholderId: {
    rule: (value) => /^[\dA-Z()]*$/.test(value),
    errorMessage:
      "Only Capital letters, brackets and number is allowed as input",
  },
  validUrl: {
    rule: (value) => /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/.test(value),
    errorMessage: "Please enter a valid URL",
  },
  validOnlinePhone: {
    rule: (value) => (/^(?=.*\d)[+\dA-Za-z\s\-\(\)\[\]\{\}\.\/\\#*;,pw]*$/i.test(value) && value.length <= 20), // lenient check
    errorMessage: "Wrong Phone Format",
  },
  validStrongPassword: {
    rule: (value) => validateStrongPassword(value),
    errorMessage: "Please enter a password with at least 12 characters, containing an uppercase, a lowercase letter, a numeric and a special character.",
  },
  validTipsOptionFormat: { //Allow trailing comma during typing
    rule: (value) => /^(\d,?){0,}$/.test(value),
    errorMessage: "Please enter comma separated numbers",
  },
  validTipsOptionFormatStrict: {
    rule: (value) => /^\d+(,\d+){0,}$/.test(value),
    errorMessage: "Please enter comma separated numbers",
  },
  validTipsOptionLength: {
    rule: (value) => value.split(",").filter(Boolean).length <= 4,
    errorMessage: "Cannot exceed 4 options",
  },

  noFullwidthCharacters: {
    rule: (value) => !/[\uff01-\uffe6]/.test(value),
    errorMessage: "Fullwidth characters is not allowed.",
  },
  noSpecialSpaces: {
    rule: (value) => !/[\u3000\u00A0]/.test(value),
    errorMessage: "Only halfwidth breaking space is allowed.",
  },
  firstCharLetterOrNumber: {
    rule: (value) => /^[a-zA-Z0-9]/.test(value),
    errorMessage: "First character must be a letter or number.",
  },

  hexColor: {
    rule: (value) => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value),
    errorMessage: "Please enter a valid hex color.",
  },
  maxTwoDecimal: {
    rule: (value) => /^[0-9]+(\.[0-9]{0,2})?$/.test(value),
    errorMessage: "Please enter a number with at most 2 decimal places.",
  }
};

/**
 * @typedef {Object} Rule
 * @property {(value: any, fieldValues: Record<string, any>) => boolean} rule
 * @property {string} errorMessage
 * @property {boolean} [falseyNoSkip=false] - Don't skip rule if value is falsey
 */

/**
 * @typedef {import("react-hook-form").UseFormReturn} formPropsType
 * @typedef {keyof formBuilderDefinedRules} definedRulesName
 */

/**
 * @return {(rule: Rule) => (value: any) => true|string}
 */
export const getRuleResolver = () => {
  return (rule) => {
    return (value) => {
      // Skip the rule if there is there's no value (Mandatory rule will catch this if the value is required)
      // Use falseyNoSkip to force the rule to run even if the value is falsey
      if (!value && !rule.falseyNoSkip) return true;

      return rule.rule(value) || rule.errorMessage;
    }
  };
};

/**
 * @param {Object} props
 * @param {boolean} props.mandatory
 * @param {number?} props.maxLength
 * @param {definedRulesName[]} props.rules
 * @param {Rule?} props.customRule
 */
export const formRulesBuilder = ({
  mandatory = false,
  minLength = undefined,
  maxLength = undefined,
  rules = [],
  customRule = undefined,
}) => {
  const result = {};
  result.required = { value: mandatory, message: mandatory ? "Mandatory" : "" };

  if (typeof minLength === "number") {
    result.minLength = {
      value: minLength,
      message: `Minimum length is ${minLength} characters.`,
    };
  } else result.minLength = undefined; //Note: All rules need to be cleared explicitly if not set, will linger otherwise

  if (typeof maxLength === "number") {
    result.maxLength = {
      value: maxLength,
      message: `Maximum length is ${maxLength} characters.`,
    };
  } else result.maxLength = undefined;

  if (rules.length > 0) {
    result.validate = {
      ...getRules(rules).map(
        getRuleResolver(),
      ).filter(Boolean),
    };
  }

  if (customRule) {
    result.validate = {
      ...result.validate,
      _customRule: (value, fieldValues) => customRule.rule(value, fieldValues) || customRule.errorMessage,
    };
  }

  if (!result.validate) result.validate = undefined;

  return result;
};
